import React, { useState, useEffect, useRef } from 'react';
import { Loader, FileText, Search, Brain } from 'lucide-react';

const PROGRESS_STAGES = [
    { duration: 2000, message: "Uploading document...", icon: FileText },
    { duration: 5000, message: "Extracting content...", icon: Search },
    { duration: 10000, message: "Analyzing contract terms...", icon: Brain },
    { duration: 15000, message: "Hold tight, almost there...", icon: Brain },
    { duration: 25000, message: "This is taking longer than usual...", icon: Brain },
    { duration: 35000, message: "Still working on it...", icon: Brain },
    { duration: Infinity, message: "Finalizing analysis...", icon: Brain }
];

export const AnalysisProgress = ({ fileName, contractType = "contract" }) => {
    const [currentStage, setCurrentStage] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const startTimeRef = useRef(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTimeRef.current;
            setElapsedTime(elapsed);
            const calculatedProgress = Math.min((elapsed / 45000) * 95, 95);
            setProgress(calculatedProgress);

            for (let i = 0; i < PROGRESS_STAGES.length; i++) {
                if (elapsed < PROGRESS_STAGES[i].duration) {
                    setCurrentStage(i);
                    break;
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);

    const stage = PROGRESS_STAGES[currentStage] || PROGRESS_STAGES[0];
    const StageIcon = stage.icon;

    return (
        <div className="w-full rounded-lg bg-white border border-slate-200 shadow-sm px-4 py-2.5 animate-fade-in">
            <div className="flex items-center gap-3">
                {/* Icon */}
                <div className="relative flex-shrink-0">
                    <div className="h-7 w-7 bg-primary-500 rounded-full flex items-center justify-center">
                        <StageIcon className="h-3.5 w-3.5 text-white animate-pulse" />
                    </div>
                </div>

                {/* Message + bar */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-slate-700 truncate">
                            {stage.message}
                        </span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                            {Math.round(progress)}% &middot; {Math.round(elapsedTime / 1000)}s
                        </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1 overflow-hidden">
                        <div
                            className="bg-primary-500 h-1 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        >
                            <div className="h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
